/**
 * SessionEventSurface
 *
 * Singleton, mounted once inside `LivekitRuntimeProvider`. Subscribes to all
 * SdkEvents from the runtime and forwards them into `livekitUiSlice` so any
 * component can react to:
 *   - session_conflict      → modal / overlay (CameraTile owns the per-tile UI)
 *   - network_state         → tile QoS pip
 *   - transport_switched    → debug telemetry
 *   - playback_clock        → seek bar (throttled ≤4/s per session)
 *   - ai_detection          → image gallery overlay
 *   - snapshot_ready        → captured-frame thumbnails
 *   - device_status         → online/offline dot on device list
 *   - signaling_connection  → top-bar indicator
 *
 * The component renders nothing — it is pure side-effect machinery.
 */
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useVgpsEvents } from '@vgps/livekit_web/react';
import {
  setNetworkState,
  setTransportFamily,
  setPlaybackClock,
  pushDetection,
  setSnapshot,
  setDeviceStatus,
  setSignalingConnection,
  setSessionConflict,
} from '../../../features/livekitUiSlice';

/** Min ms between playback_clock dispatches per session (≤4/s). */
const PLAYBACK_CLOCK_MIN_INTERVAL_MS = 250;

export function SessionEventSurface() {
  const dispatch = useDispatch();
  // playback_clock throttle state — keyed by sessionId.
  const lastClockEmitRef = useRef({});

  useVgpsEvents((event) => {
    switch (event.type) {
      case 'network_state':
        dispatch(setNetworkState({ sessionId: event.sessionId, state: event.state }));
        break;

      case 'transport_switched':
        dispatch(setTransportFamily({ sessionId: event.sessionId, family: event.family }));
        break;

      case 'playback_clock': {
        const now = Date.now();
        const last = lastClockEmitRef.current[event.sessionId] ?? 0;
        if (now - last < PLAYBACK_CLOCK_MIN_INTERVAL_MS) break;
        lastClockEmitRef.current[event.sessionId] = now;
        dispatch(setPlaybackClock({
          sessionId: event.sessionId,
          mediaTsMs: event.mediaTsMs,
          anchorTsMs: event.anchorTsMs,
          rate: event.rate,
          playing: event.playing,
        }));
        break;
      }

      case 'ai_detection':
        dispatch(pushDetection({
          key: `${event.deviceId}_${event.channelId}`,
          timestampMs: event.timestampMs,
          detections: event.detections,
        }));
        break;

      case 'snapshot_ready':
        dispatch(setSnapshot({
          key: `${event.deviceId}_${event.channelId}`,
          snapshotUrl: event.snapshotUrl,
        }));
        break;

      case 'device_status':
        dispatch(setDeviceStatus({
          deviceId: event.deviceId,
          heartbeat: event.heartbeat,
        }));
        break;

      case 'signaling_connection':
        dispatch(setSignalingConnection({ state: event.state }));
        break;

      case 'session_conflict':
        // Mirror into slice so cross-component UI (e.g. a global toast at
        // app level) can observe. CameraTile keeps its own local handling
        // (per-tile evictedByOther overlay), so this is purely additive.
        dispatch(setSessionConflict({
          sessionId: event.sessionId,
          role: event.role,
          expiresAtMs: event.expiresAtMs,
        }));
        break;

      // frame_quality, player, quality_adapted, etc. — not currently mirrored
      // into the slice. CameraTile reads frame_quality directly via useVgpsEvents.
      default:
        break;
    }
  });

  // No-op effect to make the linter happy about the missing return.
  useEffect(() => () => { lastClockEmitRef.current = {}; }, []);

  return null;
}
